import type { Deliberation } from '@/types/deliberation'
import type {
	TableauDeliberationExportFilter,
	TableauDeliberationExportPayload,
	TableauDeliberationExportSignatureMember,
	CahierDeTexteExportPayload,
} from '@/types/documentExport'
import type { PersonnelOption } from '@/types/Personnel'
import {
	formatMoyenneDisplay,
	roundMoyenne,
} from '@/utils/noteMoyenneHelpers'
import { buildEtudiantsBisByLaravelInscriptionId } from '@/utils/inscriptionIdHelpers'
import { formatNaissanceDeliberationTableau } from '@/utils/studentBirthPlaceHelpers'
import { firstDecodedNamePart } from '@/utils/decodeHtmlEntities'
import {
	fetchAllEtudiantsBisForSessionSpecialite,
	warnEtudiantsBisTruncated,
	type EtudiantsBisFetcher,
} from '@/utils/etudiantsBisPagination'

/** Résout session_id / specialite_id (champs plats ou objets imbriqués). */
export function resolveDeliberationContext(deliberation: Deliberation) {
	const sessionId = Number(
		deliberation.session_id ?? deliberation.session?.id ?? 0
	)
	const specialiteId = Number(
		deliberation.specialite_id ?? deliberation.specialite?.id ?? 0
	)
	return {
		sessionId: sessionId > 0 ? sessionId : undefined,
		specialiteId: specialiteId > 0 ? specialiteId : undefined,
	}
}

/** Charge tous les étudiants session/spécialité via Django etudiants_bis (pagination). */
export async function loadAllEtudiantsBis(
	filterInformation: EtudiantsBisFetcher,
	deliberation: Deliberation
): Promise<any[]> {
	const { sessionId, specialiteId } = resolveDeliberationContext(deliberation)
	if (!sessionId || !specialiteId) return []

	const { results, truncated } = await fetchAllEtudiantsBisForSessionSpecialite(
		filterInformation,
		sessionId,
		specialiteId
	)
	warnEtudiantsBisTruncated(truncated, 'document')
	return results
}

function formatTxReussite(tx: number | string): string {
	if (typeof tx === 'string' && tx.includes('%')) return tx
	const n = typeof tx === 'number' ? tx : Number.parseFloat(String(tx))
	if (!Number.isFinite(n)) return '0.00%'
	return `${roundMoyenne(n).toFixed(2)}%`
}

function formatMoyenneClasse(v: number): string {
	return formatMoyenneDisplay(v)
}

function personnelDisplayName(p: PersonnelOption | undefined): string {
	if (!p) return ''
	const nom = p.personne?.nom || ''
	const prenom = p.personne?.prenom || ''
	return `${nom} ${prenom}`.trim()
}

function normalizeSignatureMemberEntry(
	entry: TableauDeliberationExportSignatureMember | number
): { personnelId: number; matieres: string[] } {
	if (typeof entry === 'number') {
		return { personnelId: entry, matieres: [] }
	}
	return {
		personnelId: Number(entry.personnel_id),
		matieres: Array.isArray(entry.matieres)
			? entry.matieres.map((m) => String(m).trim()).filter(Boolean)
			: [],
	}
}

function mapSignatureMembers(
	members: (TableauDeliberationExportSignatureMember | number)[],
	personnelById: Map<number, PersonnelOption>
) {
	return members.map((entry) => {
		const { personnelId, matieres } = normalizeSignatureMemberEntry(entry)
		const p = personnelId ? personnelById.get(personnelId) : undefined
		const line2 = matieres.join(', ')
		const mapped: { line1: string; line2?: string; line2Italic?: boolean } = {
			line1: personnelDisplayName(p),
		}
		if (line2) {
			mapped.line2 = line2
			mapped.line2Italic = line2.length > 35
		}
		return mapped
	})
}

/** Mappe le payload backend + enrichissements Django vers les props DeliberationTemplate. */
export function mapTableauDeliberationExport(
	payload: TableauDeliberationExportPayload,
	etudiantsBis: any[],
	deliberation: Deliberation,
	personnels: PersonnelOption[],
	filter?: TableauDeliberationExportFilter
) {
	const etuMap = buildEtudiantsBisByLaravelInscriptionId(etudiantsBis)
	const personnelById = new Map(personnels.map((p) => [p.id, p]))

	const ueColumns = payload.ues.map((ue) => ({
		code: ue.ue_code || `UE`,
		label: ue.ue_code || 'UE',
	}))

	const ueBlocks = payload.ues.map((ue) => ({
		title: ue.title_formatted,
		ecs: ue.ecs.map((ec) => ({
			nom: ec.intitule,
			coeff: ec.coefficient,
			moy: formatMoyenneClasse(ec.moyenne_classe),
		})),
	}))

	const students = [...payload.students]
		.map((s) => {
			const etu = etuMap.get(Number(s.inscription_id))
			const personne = etu?.personne || etu
			const matricule =
				String(
					etu?.matricule ||
						etu?.code ||
						etu?.n_num_etudiant ||
						personne?.matricule ||
						''
				).trim() || `ETU-${s.inscription_id}`
			const nom = firstDecodedNamePart(etu?.personne?.nom, etu?.n_nom, personne?.nom)
			const prenom = firstDecodedNamePart(etu?.personne?.prenom, etu?.n_prenom, personne?.prenom)
			return {
				inscription_id: s.inscription_id,
				matricule,
				nom: nom.toUpperCase(),
				prenom,
				naissance: formatNaissanceDeliberationTableau(etu || personne),
				ues: s.ues.map((u) => ({
					moyenne: u.moyenne_formatted || '',
					validation: u.validation || '',
				})),
				totalCredits: s.total_credits,
				moyenneGenerale: s.moyenne_generale_formatted || '',
				appreciation: s.appreciation || '',
			}
		})
		.sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' }))

	const ueValidation = payload.ue_validation_stats.map((u, i) => ({
		code: payload.ues[i]?.ue_code || `UE${i + 1}`,
		valide: u.valide,
		nonValide: u.non_valide,
		txReussite: formatTxReussite(u.tx_reussite),
	}))

	const presidentId = payload.president_jury
	const presidentPersonnel = presidentId ? personnelById.get(presidentId) : undefined
	const presidentJury =
		filter?.includePresident === false
			? ''
			: personnelDisplayName(presidentPersonnel) || ''

	let signatureMembersRaw = payload.signature_members || []
	// Tableau vide = exclusion volontaire de tous les profs ; undefined = pas de filtre (tous inclus)
	if (filter && Array.isArray(filter.selectedPersonnelIds)) {
		const allowed = new Set(filter.selectedPersonnelIds)
		signatureMembersRaw = signatureMembersRaw.filter((entry) => {
			const { personnelId } = normalizeSignatureMemberEntry(entry)
			return allowed.has(personnelId)
		})
	}

	const signatureMembers = mapSignatureMembers(signatureMembersRaw, personnelById)

	const ueCount = ueColumns.length
	const tableFontScale =
		ueCount <= 4 ? 1 : ueCount <= 5 ? 0.94 : ueCount <= 6 ? 0.88 : 0.82

	return {
		header: payload.header,
		ueColumns,
		ueBlocks,
		students,
		appreciationStats: payload.appreciation_stats,
		ueValidation,
		effectif: payload.effectif,
		lieuDate: payload.lieu_date,
		presidentJury,
		signatureMembers,
		footerRef: payload.footer_ref,
		tableFontScale,
		ueCount,
	}
}

export function mapCahierDeTexteExport(
	payload: CahierDeTexteExportPayload,
	personnels: PersonnelOption[]
) {
	const personnel = payload.info.personnel_id
		? personnels.find((p) => p.id === payload.info.personnel_id)
		: undefined

	const profNom = personnel
		? `${personnel.personne?.nom || ''} ${personnel.personne?.prenom || ''}`.trim()
		: ''

	const sessionMeta = payload.info.session
	const sessionNom =
		sessionMeta?.nom?.trim() || sessionMeta?.code?.trim() || ''
	const anneeUniversitaire = sessionMeta?.annee_universitaire?.trim() || ''

	return {
		info: {
			semestre: payload.info.semestre,
			ue: payload.info.ue_intitule,
			ec: payload.info.ec_intitule,
			volumeHoraire: payload.info.volume_horaire,
			sessionNom,
			anneeUniversitaire,
			profNom,
			profEmail: personnel?.personne?.email || '',
			profTel: '',
		},
		sessions: payload.sessions.map((s) => ({
			date: s.date,
			debut: s.heure_debut,
			fin: s.heure_fin,
			duree: s.duree_formatted,
			cumul: s.cumul_formatted,
			titre: s.cours_label,
			contenu: (s.contenu || '').split('\n'),
		})),
	}
}

/**
 * Répartition des séances par page (tailles de tranches, pas indices cumulatifs).
 * Page 1 : en-tête + 4 séances · page 2 : 5 · suivantes : 6 · dernière : signature.
 */
export function computeCahierPageBreaks(sessionCount: number): number[] {
	if (sessionCount <= 4) return []

	const breaks: number[] = [4]
	let remaining = sessionCount - 4

	const second = Math.min(5, remaining)
	breaks.push(second)
	remaining -= second

	while (remaining > 0) {
		breaks.push(Math.min(6, remaining))
		remaining -= breaks[breaks.length - 1]
	}

	return breaks
}
